import { useModuleAccessIfAvailable } from '../../../contexts/ModuleContext';

/**
 * The module whose entitlement the server demands before it will store a geocode result:
 * `shared_saveGeocodeResult` is gated `withModuleAccess("P6")` (Central Command).
 */
export const GEOCODE_SAVE_MODULE = 'P6' as const;

/**
 * Whether this user should send a save to the shared geocode cache.
 *
 * The save is fire-and-forget and the server refuses everyone without P6 with a 403 that the user never sees and
 * Sentry records as a generic "Network error". Skipping the call changes nothing visible: the geocoded position is
 * still shown. The server gate stays as it is on purpose: letting any member write the shared cache would let one
 * account poison it for everyone.
 *
 * This is deliberately a little NARROWER than the server: the client's owner shortcut is the fixed owner e-mail list,
 * while the server also accepts a user whose stored role is Owner. Such a user without P6 skips a save the server
 * would have taken (a missed cache write, nothing else). In the other direction the client may say yes where the
 * server says no (a deactivated or outside-contractor account); that is today's behaviour, a harmless 403.
 *
 * While the signed-in profile has not loaded, `hasModuleAccess` answers false, so a geocode that finishes before the
 * profile arrives skips its save. With NO ModuleProvider mounted (tests, stubs) there is nothing to read, so the
 * save is attempted as before (the server still decides).
 */
export function useCanSaveGeocode(): boolean {
  const hasModuleAccess = useModuleAccessIfAvailable();
  return hasModuleAccess ? hasModuleAccess(GEOCODE_SAVE_MODULE) : true;
}
